import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Braces,
  Clock3,
  Gauge,
  LockKeyhole,
  Music2,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Youtube,
} from "lucide-react";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import {
  CreateSongV2Data,
  SongV2,
  SongV2Chart,
  SongV2Lane,
  createSongV2,
  fetchSongV2ById,
  updateSongV2,
} from "./songV2Api";
import styles from "./SongV2Shared.module.css";

type SongForm = {
  v: string;
  title: string;
  artist: string;
  bpm: string;
  ts: string;
  duration_ms: string;
  source_type: string;
  source_url: string;
  video_id: string;
  sync_ms: string;
  lookahead_ms: string;
  hit_ms: string;
  calibrate_speed: string;
  lanes: string;
  dt: string;
  m: string;
  chart: string;
  is_locked: boolean;
};

const defaultLanes: SongV2Lane[] = [
  { lane: 0, name: "Hi-Hat", midi_notes: [42, 44, 46] },
  { lane: 1, name: "Crash", midi_notes: [49, 57] },
  { lane: 2, name: "Ride", midi_notes: [51, 53] },
  { lane: 3, name: "Snare", midi_notes: [38, 40] },
  { lane: 4, name: "Tom 1", midi_notes: [48, 50] },
  { lane: 5, name: "Tom 2", midi_notes: [45, 47] },
  { lane: 6, name: "Floor Tom", midi_notes: [41, 43] },
  { lane: 7, name: "Kick", midi_notes: [35, 36] },
];

const emptyChart: SongV2Chart = {
  format: "score_v2",
  ppq: 480,
  tempo_map: [{ tick: 0, bpm: 120 }],
  events: [],
};

const emptyForm = (): SongForm => ({
  v: "1",
  title: "",
  artist: "",
  bpm: "120",
  ts: "4/4",
  duration_ms: "180000",
  source_type: "youtube",
  source_url: "",
  video_id: "",
  sync_ms: "0",
  lookahead_ms: "2200",
  hit_ms: "80",
  calibrate_speed: "1",
  lanes: JSON.stringify(defaultLanes, null, 2),
  dt: "[]",
  m: "[]",
  chart: JSON.stringify(emptyChart, null, 2),
  is_locked: false,
});

function songToForm(song: SongV2): SongForm {
  return {
    v: String(song.v),
    title: song.title,
    artist: song.artist,
    bpm: String(song.bpm),
    ts: song.ts,
    duration_ms: String(song.duration_ms),
    source_type: song.source.type,
    source_url: song.source.url,
    video_id: song.source.video_id,
    sync_ms: String(song.sync_ms),
    lookahead_ms: String(song.lookahead_ms),
    hit_ms: String(song.hit_ms),
    calibrate_speed: String(song.calibrate_speed),
    lanes: JSON.stringify(song.lanes, null, 2),
    dt: JSON.stringify(song.dt),
    m: JSON.stringify(song.m),
    chart: JSON.stringify(song.chart, null, 2),
    is_locked: song.is_locked,
  };
}

function extractVideoId(value: string) {
  const match = value.match(
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match?.[1] || "";
}

export default function SongEditor({ mode }: { mode: "create" | "edit" }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const access = getCurrentTokenAccessInfo();
  const canWrite = Boolean(access?.hasPanelAccess);
  const [form, setForm] = useState<SongForm>(emptyForm);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    fetchSongV2ById(id)
      .then((song) => setForm(songToForm(song)))
      .catch((err: any) =>
        setError(err?.message || "V2 şarkı bilgileri yüklenemedi.")
      )
      .finally(() => setLoading(false));
  }, [id, mode]);

  const update = <K extends keyof SongForm>(key: K, value: SongForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const updateSourceUrl = (value: string) => {
    const detectedId = extractVideoId(value);
    setForm((current) => ({
      ...current,
      source_url: value,
      video_id: detectedId || current.video_id,
    }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canWrite) return;
    if (!form.title.trim() || !form.artist.trim()) {
      setError("Başlık ve sanatçı zorunludur.");
      return;
    }
    if (Number(form.bpm) < 1 || Number(form.duration_ms) < 0) {
      setError("BPM en az 1 olmalı ve süre negatif olmamalıdır.");
      return;
    }
    if (!form.source_type.trim() || !form.source_url.trim()) {
      setError("Kaynak tipi ve URL zorunludur.");
      return;
    }

    let lanes: SongV2Lane[];
    let dt: number[];
    let m: number[];
    let chart: SongV2Chart;
    try {
      lanes = JSON.parse(form.lanes);
      dt = JSON.parse(form.dt);
      m = JSON.parse(form.m);
      chart = JSON.parse(form.chart);
    } catch {
      setError("Lanes, dt, m ve chart alanları geçerli JSON olmalıdır.");
      return;
    }
    if (!Array.isArray(lanes) || !Array.isArray(dt) || !Array.isArray(m)) {
      setError("Lanes, dt ve m alanları JSON dizisi olmalıdır.");
      return;
    }
    if (dt.length !== m.length) {
      setError(`dt (${dt.length}) ve m (${m.length}) uzunlukları eşit olmalıdır.`);
      return;
    }
    if (
      !chart ||
      typeof chart !== "object" ||
      !Array.isArray(chart.tempo_map) ||
      !Array.isArray(chart.events)
    ) {
      setError("Chart; format, ppq, tempo_map ve events alanlarını içermelidir.");
      return;
    }

    const payload: CreateSongV2Data = {
      v: Number(form.v) || 1,
      title: form.title.trim(),
      artist: form.artist.trim(),
      bpm: Number(form.bpm),
      ts: form.ts.trim(),
      duration_ms: Number(form.duration_ms),
      source: {
        type: form.source_type.trim(),
        url: form.source_url.trim(),
        video_id: form.video_id.trim(),
      },
      sync_ms: Number(form.sync_ms),
      lookahead_ms: Number(form.lookahead_ms),
      hit_ms: Number(form.hit_ms),
      calibrate_speed: Number(form.calibrate_speed),
      lanes,
      dt: dt.map(Number),
      m: m.map(Number),
      chart,
      is_locked: form.is_locked,
    };

    try {
      setSaving(true);
      setError("");
      const song =
        mode === "edit" && id
          ? await updateSongV2(id, payload)
          : await createSongV2(payload);
      navigate(`/songs/${song.songv2_id}`, {
        replace: mode === "edit",
        state: {
          message:
            mode === "edit"
              ? "V2 şarkı başarıyla güncellendi."
              : "V2 şarkı başarıyla oluşturuldu.",
        },
      });
    } catch (err: any) {
      setError(err?.message || "V2 şarkı kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={styles.songState}><div className={styles.songLoader} /><h2>V2 şarkı hazırlanıyor</h2><p>Tam chart verisi yükleniyor.</p></div>;
  }

  return (
    <div className={styles.songPage}>
      <header className={styles.editorHero}>
        <Link to={id ? `/songs/${id}` : "/songs"}><ArrowLeft size={17} /></Link>
        <div><span><Music2 size={14} /> V2 şarkı merkezi</span><h1>{mode === "edit" ? "V2 şarkıyı düzenle" : "Yeni V2 şarkı"}</h1><p>Kaynak, senkronizasyon, lane ve chart verilerini birlikte yönetin.</p></div>
        <span className={styles.accessBadge}><ShieldCheck size={14} />{access?.roles.join(" · ") || "SALT OKUNUR"}</span>
      </header>

      {error && <div className={styles.songFeedbackError}><AlertCircle size={17} /> {error}</div>}
      {!canWrite ? (
        <div className={styles.songFeedbackError}><LockKeyhole size={17} /> Bu işlem için panel rolü gerekir.</div>
      ) : (
        <form className={styles.songEditorForm} onSubmit={submit}>
          <section className={styles.songPanel}>
            <div className={styles.panelHeading}><div><span>Kimlik</span><h2>Temel V2 alanları</h2></div><Music2 size={18} /></div>
            <div className={styles.songFormGrid}>
              <label><span>Başlık <em>*</em></span><input autoFocus value={form.title} onChange={(e) => update("title", e.target.value)} required /></label>
              <label><span>Sanatçı <em>*</em></span><input value={form.artist} onChange={(e) => update("artist", e.target.value)} required /></label>
              <label><span>Model versiyonu</span><input type="number" min="1" value={form.v} onChange={(e) => update("v", e.target.value)} /></label>
              <label><span><Gauge size={13} /> BPM</span><input type="number" min="1" value={form.bpm} onChange={(e) => update("bpm", e.target.value)} required /></label>
              <label><span>Time signature</span><input value={form.ts} onChange={(e) => update("ts", e.target.value)} placeholder="4/4" required /></label>
              <label><span><Clock3 size={13} /> Süre (ms)</span><input type="number" min="0" value={form.duration_ms} onChange={(e) => update("duration_ms", e.target.value)} required /></label>
            </div>
          </section>

          <section className={styles.songPanel}>
            <div className={styles.panelHeading}><div><span>Kaynak</span><h2>Medya kaynağı</h2></div><Youtube size={18} /></div>
            <div className={styles.songFormGrid}>
              <label><span>Kaynak tipi</span><input value={form.source_type} onChange={(e) => update("source_type", e.target.value)} required /></label>
              <label><span>Video ID</span><input value={form.video_id} onChange={(e) => update("video_id", e.target.value)} /></label>
              <label className={styles.songFullField}><span>Kaynak URL <em>*</em></span><input value={form.source_url} onChange={(e) => updateSourceUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." required /></label>
            </div>
          </section>

          <section className={styles.songPanel}>
            <div className={styles.panelHeading}><div><span>Oynatma</span><h2>Senkronizasyon ayarları</h2></div><SlidersHorizontal size={18} /></div>
            <div className={styles.songFormGrid}>
              <label><span>Sync (ms)</span><input type="number" value={form.sync_ms} onChange={(e) => update("sync_ms", e.target.value)} /></label>
              <label><span>Lookahead (ms)</span><input type="number" min="0" value={form.lookahead_ms} onChange={(e) => update("lookahead_ms", e.target.value)} /></label>
              <label><span>Hit window (ms)</span><input type="number" min="0" value={form.hit_ms} onChange={(e) => update("hit_ms", e.target.value)} /></label>
              <label><span>Kalibrasyon hızı</span><input type="number" min="0" step="0.01" value={form.calibrate_speed} onChange={(e) => update("calibrate_speed", e.target.value)} /></label>
            </div>
          </section>

          <section className={styles.songPanel}>
            <div className={styles.panelHeading}><div><span>Chart</span><h2>Lane ve nota verileri</h2></div><Braces size={18} /></div>
            <div className={styles.songFormGrid}>
              <label className={styles.songFullField}><span>Lanes JSON</span><textarea rows={12} value={form.lanes} onChange={(e) => update("lanes", e.target.value)} spellCheck={false} /></label>
              <label><span>dt JSON <small>{form.dt ? `${(() => { try { return JSON.parse(form.dt).length; } catch { return "?"; } })()} kayıt` : "0 kayıt"}</small></span><textarea rows={9} value={form.dt} onChange={(e) => update("dt", e.target.value)} spellCheck={false} /></label>
              <label><span>m JSON <small>{form.m ? `${(() => { try { return JSON.parse(form.m).length; } catch { return "?"; } })()} kayıt` : "0 kayıt"}</small></span><textarea rows={9} value={form.m} onChange={(e) => update("m", e.target.value)} spellCheck={false} /></label>
              <label className={styles.songFullField}><span>Chart JSON</span><textarea rows={16} value={form.chart} onChange={(e) => update("chart", e.target.value)} spellCheck={false} /></label>
            </div>
          </section>

          <section className={styles.songEditorFooter}>
            <label className={styles.songToggle}><input type="checkbox" checked={form.is_locked} onChange={(e) => update("is_locked", e.target.checked)} /><span><i /></span><div><strong>Kilitli içerik</strong><small>Atama veya yetki gerektiren şarkı.</small></div></label>
            <div><Link to={id ? `/songs/${id}` : "/songs"}>İptal</Link><button type="submit" disabled={saving}><Save size={16} />{saving ? "Kaydediliyor" : "V2 şarkıyı kaydet"}</button></div>
          </section>
        </form>
      )}
    </div>
  );
}
